import type { TGooglePlaceSuggestion } from "@/api/google-maps/search/google-search.type.ts";
import type {
  IAddressProviderVisitor,
  TAddressProviderVisitorOutput,
} from "@/domains/address/providers/address-provider-visitor.ts";
import type { IAddressProvider } from "@/domains/address/providers/address-provider.ts";
import type { ECountryCode } from "@/domains/country/country.types.ts";
import type { TGoogleMapsLanguageCode } from "@/lib/google.ts";

import { normalizeGoogleMapsError } from "@/api/api-error-normalization.ts";
import { findCountryDefinitionByCountry } from "@/countries/country-definition.ts";
import { GoogleAddress } from "@/domains/address/providers/google/google-address.ts";
import { ECountry } from "@/domains/country/country.types.ts";
import { isUnexpectedError } from "@/lib/error-reporting.ts";
import { EGoogleAddressType, getPlaces } from "@/lib/google.ts";
import { captureNonBlockingError } from "@/lib/observability.ts";
import { normalizeString } from "@/lib/utils.ts";
import { isDefined } from "@/types/utils";

function filterNoPremiseNoStreet(item: google.maps.places.Place) {
  const whitelist = [EGoogleAddressType.PREMISE, EGoogleAddressType.ROUTE];
  return !!item.addressComponents?.find((ac) => {
    return ac.types.find((type) => {
      return whitelist.includes(type as EGoogleAddressType);
    });
  });
}

function filterOutByCountryCode(countryCode: ECountryCode) {
  return (item: google.maps.places.Place) => {
    const country = item.addressComponents?.find((ac) => ac.types.includes(EGoogleAddressType.COUNTRY));

    if (!country) {
      // Idk if we should filter this case out or if we want to tolerate unknown country ?
      // This case is not supposed to happen, so by default I decided to allow them, but ?????
      return true;
    }

    return normalizeString(country.shortText) === normalizeString(countryCode);
  };
}

function convertCountryToLanguageCode(country: ECountry): TGoogleMapsLanguageCode {
  switch (country) {
    case ECountry.FRANCE:
    case ECountry.BELGIUM:
      return "fr";
    case ECountry.FINLAND:
      return "fi";
    case ECountry.SLOVENIA:
      return "sl";
    case ECountry.SWEDEN:
      return "sv";
    case ECountry.GREAT_BRITAIN:
      return "en-GB";
    case ECountry.SPAIN:
      return "es";
    case ECountry.CZECH:
      return "cs";
    case ECountry.HUNGARY:
      return "hu";
    default:
      return "fr";
  }
}

export const GoogleAddressProvider: IAddressProvider = {
  accept<V extends IAddressProviderVisitor>(visitor: V): TAddressProviderVisitorOutput<V> {
    return visitor.visitGoogle() as TAddressProviderVisitorOutput<V>;
  },

  async search(query: string, country: ECountry = ECountry.FRANCE): Promise<Array<GoogleAddress>> {
    try {
      const language = convertCountryToLanguageCode(country);
      const { countryCode } = findCountryDefinitionByCountry(country, true);

      const request: google.maps.places.SearchByTextRequest = {
        textQuery: query,
        fields: ["id", "formattedAddress", "addressComponents", "location"],
        language: language,
        region: countryCode,
        maxResultCount: 20,
      };

      const { Place } = await getPlaces();
      const { places } = await Place.searchByText(request);

      return places
        .filter(filterOutByCountryCode(countryCode))
        .filter(filterNoPremiseNoStreet)
        .map(toKnownGooglePlaceSuggestion)
        .filter(isDefined)
        .map((k) => new GoogleAddress(k));
    } catch (error) {
      const normalizedError = normalizeGoogleMapsError(error);
      if (isUnexpectedError(normalizedError)) {
        captureNonBlockingError(normalizedError);
      }
      return [];
    }
  },
};

// Avoid relying on Place.toJSON() because it may change and break our backend compatibility.
function toKnownGooglePlaceSuggestion(p: google.maps.places.Place): TGooglePlaceSuggestion | undefined {
  const latitude = p.location?.lat();
  const longitude = p.location?.lng();

  if (latitude === undefined || longitude === undefined) {
    return undefined;
  }

  return {
    id: p.id,
    formattedAddress: p.formattedAddress ?? undefined,
    addressComponents: p.addressComponents ?? [],
    location: { latitude, longitude },
  };
}
